export const LINE_STATUSES = [
  { value: "Proposition", label: "Proposition" },
  { value: "En cours", label: "En cours" },
  { value: "A facturer", label: "A facturer" },
  { value: "Facturé", label: "Facture" },
  { value: "Perdu", label: "Perdu" },
];

export const INVOICE_STATUSES = [
  { value: "Prevu", label: "Prévu" },
  { value: "Facture", label: "Envoyée" },
];

export const BILLABILITY_STATUSES = [
  { value: "pending", label: "A valider" },
  { value: "billable", label: "Facturable" },
  { value: "not_billable", label: "Non facturable" },
];

export const DATE_ANCHORS = [
  { value: "fixed", label: "Date fixe" },
  { value: "iteration_start", label: "Debut iteration" },
  { value: "iteration_end", label: "Fin iteration" },
];

// Conditions de règlement reconnues par le backend (cf. PaymentTerms / _payment_due_date_sql).
// Toute autre valeur retombe sur « 45 jours fin de mois » côté calcul des encaissements.
export const PAYMENT_TERMS = [
  "à réception",
  "30 jours nets",
  "45 jours nets",
  "45 jours fin de mois",
];

export const todayIso = () => new Date().toISOString().slice(0, 10);

export const roundMoney = (value) => Math.round(Number(value || 0) * 100) / 100;

export const parseDecimal = (value) => {
  const parsed = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : NaN;
};

export const formatMoney = (value) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(value || 0));

export const formatQty = (value) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 3 }).format(Number(value || 0));

export const formatDate = (value) => {
  if (!value) return "-";
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(`${value}T00:00:00`));
};

export const statusClass = (status) => {
  switch (status) {
    case "Envoyee":
    case "Paye":
    case "Facture":
      return "border-blue-200 bg-blue-50 text-blue-700";
    case "Annule":
      return "border-slate-200 bg-slate-100 text-slate-500";
    default:
      return "border-amber-200 bg-amber-50 text-amber-700";
  }
};

export const batchStateClass = (state) => {
  switch (state) {
    case "En cours":
      return "border-blue-200 bg-blue-50 text-blue-700";
    case "Clos":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "Perdu":
      return "border-rose-200 bg-rose-50 text-rose-600";
    default:
      return "border-slate-200 bg-slate-100 text-slate-600";
  }
};

export const invoiceStatusLabel = (status) => {
  if (status === "Prevu") return "Prévu";
  return "Envoyée";
};

export const billabilityStatusLabel = (status) => {
  switch (status) {
    case "billable":
      return "Facturable";
    case "not_billable":
      return "Non facturable";
    case "invoiced":
      return "Facturee";
    default:
      return "A valider";
  }
};

export const billabilityStatusClass = (status) => {
  switch (status) {
    case "billable":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "not_billable":
      return "border-rose-200 bg-rose-50 text-rose-600";
    case "invoiced":
      return "border-blue-200 bg-blue-50 text-blue-700";
    default:
      return "border-slate-200 bg-slate-50 text-slate-500";
  }
};

export const normalizedInvoiceStatusValue = (status) =>
  INVOICE_STATUSES.some((item) => item.value === status) ? status : "Facture";

export const isSentInvoiceStatus = (status) => status === "Facture" || status === "Paye";

export const isPlannedInvoiceStatus = (status) => status === "Prevu";

export const isActiveInvoiceEvent = (event) => event?.direction !== "outflow" && event?.status !== "Annule";

export const getInvoiceAmountTotals = (cashEvents = []) => {
  return cashEvents.filter(isActiveInvoiceEvent).reduce(
    (totals, event) => {
      const amount = Number(event.amount_eur || 0);
      if (isSentInvoiceStatus(event.status)) {
        totals.invoiced += amount;
      } else if (isPlannedInvoiceStatus(event.status)) {
        totals.planned += amount;
      }
      totals.linked += amount;
      return totals;
    },
    { linked: 0, planned: 0, invoiced: 0 },
  );
};

export const getLineAmount = (line) => Number(line?.unit_price || 0) * Number(line?.quantity || 0);

export const getEventLineIds = (event) => {
  const ids = (event?.line_allocations || []).map((item) => item.line_id);
  if (!ids.length && event?.line_id) ids.push(event.line_id);
  return ids.filter(Boolean);
};

export const getConsumedQuantities = (cashEvents = [], lines = [], excludeEventId = null) => {
  const unitPriceByLine = new Map(lines.map((line) => [String(line.id), Number(line.unit_price || 0)]));
  const consumed = new Map();
  cashEvents
    .filter(
      (event) =>
        event.direction !== "outflow" &&
        event.status !== "Annule" &&
        (excludeEventId == null || event.id !== excludeEventId),
    )
    .forEach((event) => {
      (event.line_allocations || []).forEach((allocation) => {
        const key = String(allocation.line_id);
        const unitPrice = unitPriceByLine.get(key) || 0;
        const quantity =
          allocation.allocated_quantity !== null && allocation.allocated_quantity !== undefined
            ? Number(allocation.allocated_quantity || 0)
            : unitPrice > 0
              ? Number(allocation.allocated_amount_eur || 0) / unitPrice
              : 0;
        consumed.set(key, roundMoney((consumed.get(key) || 0) + quantity));
      });
    });
  return consumed;
};

export const getLineInvoiceBreakdown = (cashEvents = [], lines = [], excludeEventId = null) => {
  const unitPriceByLine = new Map(lines.map((line) => [String(line.id), Number(line.unit_price || 0)]));
  const breakdown = new Map();

  const ensureLine = (lineId) => {
    const key = String(lineId);
    if (!breakdown.has(key)) {
      breakdown.set(key, {
        linkedAmount: 0,
        plannedAmount: 0,
        invoicedAmount: 0,
        linkedQuantity: 0,
        plannedQuantity: 0,
        invoicedQuantity: 0,
      });
    }
    return breakdown.get(key);
  };

  cashEvents
    .filter((event) => isActiveInvoiceEvent(event) && (excludeEventId == null || event.id !== excludeEventId))
    .forEach((event) => {
      const allocations = (event.line_allocations || []).length
        ? event.line_allocations
        : event.line_id
          ? [{ line_id: event.line_id, allocated_amount_eur: event.amount_eur }]
          : [];

      allocations.forEach((allocation) => {
        const key = String(allocation.line_id);
        const unitPrice = unitPriceByLine.get(key) || 0;
        const amount = Number(allocation.allocated_amount_eur || 0);
        const quantity =
          allocation.allocated_quantity !== null && allocation.allocated_quantity !== undefined
            ? Number(allocation.allocated_quantity || 0)
            : unitPrice > 0
              ? amount / unitPrice
              : 0;
        const lineBreakdown = ensureLine(key);
        lineBreakdown.linkedAmount = roundMoney(lineBreakdown.linkedAmount + amount);
        lineBreakdown.linkedQuantity = roundMoney(lineBreakdown.linkedQuantity + quantity);
        if (isSentInvoiceStatus(event.status)) {
          lineBreakdown.invoicedAmount = roundMoney(lineBreakdown.invoicedAmount + amount);
          lineBreakdown.invoicedQuantity = roundMoney(lineBreakdown.invoicedQuantity + quantity);
        } else if (isPlannedInvoiceStatus(event.status)) {
          lineBreakdown.plannedAmount = roundMoney(lineBreakdown.plannedAmount + amount);
          lineBreakdown.plannedQuantity = roundMoney(lineBreakdown.plannedQuantity + quantity);
        }
      });
    });

  return breakdown;
};

export const buildOrderInvoiceFlow = (lines = [], invoiceEvents = [], consumedByLine = new Map()) => {
  const rowHeight = 92;
  const nodeHeight = 80;
  const leftMap = new Map();
  const rightNodes = invoiceEvents.map((invoice) => ({
    key: String(invoice.id),
    invoice,
    label: invoice.invoice_reference || invoice.label || `Facture ${invoice.id}`,
    amount: Number(invoice.amount_eur || 0),
    sortDate: invoice.issue_date || invoice.event_date || invoice.effective_event_date || "",
  }));
  const flowMap = new Map();
  const breakdownByLine = getLineInvoiceBreakdown(invoiceEvents, lines);

  lines.forEach((line, index) => {
    const consumed = consumedByLine.get(String(line.id)) || 0;
    const remaining = Math.max(Number(line.quantity || 0) - consumed, 0);
    const lineAmount = getLineAmount(line);
    const breakdown = breakdownByLine.get(String(line.id)) || {};
    const allocationLabel = (line.allocations || [])
      .map((allocation) =>
        [allocation.project_number, allocation.project_name, allocation.iteration_name]
          .filter(Boolean)
          .join(" / "),
      )
      .filter(Boolean)
      .join(" - ");
    leftMap.set(String(line.id), {
      key: String(line.id),
      line,
      label: line.name,
      sublabel: allocationLabel || "Commande",
      amount: lineAmount,
      consumed,
      remaining,
      linkedAmount: Number(breakdown.linkedAmount || 0),
      plannedAmount: Number(breakdown.plannedAmount || 0),
      invoicedAmount: Number(breakdown.invoicedAmount || 0),
      unlinkedAmount: Math.max(roundMoney(lineAmount - Number(breakdown.linkedAmount || 0)), 0),
      order: index,
    });
  });

  const ensureLeftNode = (lineId, label) => {
    const key = String(lineId || "global");
    if (!leftMap.has(key)) {
      leftMap.set(key, {
        key,
        line: null,
        label: label || "Non ventilé",
        sublabel: "Sans ligne de commande",
        amount: 0,
        consumed: 0,
        remaining: 0,
        order: leftMap.size + 1000,
      });
    }
    return leftMap.get(key);
  };

  const addFlow = (lineId, invoiceId, amount, lineName) => {
    const value = Math.abs(Number(amount || 0));
    if (value <= 0.01) return;
    const sourceKey = String(lineId || "global");
    const targetKey = String(invoiceId);
    const key = `${sourceKey}|${targetKey}`;
    if (!flowMap.has(key)) {
      flowMap.set(key, { key, sourceKey, targetKey, amount: 0, lineNames: new Set() });
    }
    const flow = flowMap.get(key);
    flow.amount = roundMoney(flow.amount + value);
    if (lineName) flow.lineNames.add(lineName);
  };

  rightNodes.forEach((node) => {
    const allocations = (node.invoice.line_allocations || []).length
      ? node.invoice.line_allocations.map((allocation) => ({
          lineId: allocation.line_id,
          lineName: allocation.line_name,
          amount: allocation.allocated_amount_eur,
        }))
      : node.invoice.line_id
        ? [{ lineId: node.invoice.line_id, lineName: node.invoice.line_name, amount: node.invoice.amount_eur }]
        : [];

    if (!allocations.length) {
      ensureLeftNode("global", "Non ventilé");
      addFlow("global", node.key, node.amount, "Non ventilé");
      return;
    }

    allocations.forEach((allocation) => {
      ensureLeftNode(allocation.lineId, allocation.lineName || `Ligne ${allocation.lineId}`);
      addFlow(allocation.lineId, node.key, allocation.amount, allocation.lineName);
    });
  });

  const leftNodes = Array.from(leftMap.values()).sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
  const orderedRightNodes = [...rightNodes].sort((a, b) =>
    `${a.sortDate}-${a.label}`.localeCompare(`${b.sortDate}-${b.label}`),
  );
  const height = Math.max(120, Math.max(leftNodes.length, orderedRightNodes.length, 1) * rowHeight);
  const positionNodes = (nodes) => {
    if (!nodes.length) return [];
    const step = height / nodes.length;
    return nodes.map((node, index) => ({ ...node, y: index * step + step / 2 }));
  };
  const positionedLeftNodes = positionNodes(leftNodes);
  const positionedRightNodes = positionNodes(orderedRightNodes);
  const leftY = new Map(positionedLeftNodes.map((node) => [node.key, node.y]));
  const rightY = new Map(positionedRightNodes.map((node) => [node.key, node.y]));
  const flows = Array.from(flowMap.values());
  const maxFlow = Math.max(...flows.map((flow) => flow.amount), 1);

  return {
    height,
    nodeHeight,
    leftNodes: positionedLeftNodes,
    rightNodes: positionedRightNodes,
    flows: flows.map((flow) => ({
      ...flow,
      lineNames: Array.from(flow.lineNames),
      sourceY: leftY.get(flow.sourceKey) || height / 2,
      targetY: rightY.get(flow.targetKey) || height / 2,
      width: Math.max(2.5, Math.min(16, 2.5 + (flow.amount / maxFlow) * 13.5)),
    })),
  };
};

export const createEmptyInvoiceDraft = () => ({
  invoice_reference: "",
  label: "",
  status: "Prevu",
  billability_status: "pending",
  issue_date_anchor: "fixed",
  issue_date: todayIso(),
  issue_project_id: "",
  issue_iteration_id: "",
  payment_terms_override: "",
  lines: {},
});
